import { Grid, Paper } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { ItemForm, SuggestionField, TextField } from 'cleveron-web-components';
import React, { useEffect, useMemo } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { Field } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import {
    deleteLocationInfoAttachment as deleteLocationInfoAttachmentAction,
    uploadLocationInfoAttachment as uploadLocationInfoAttachmentAction,
} from '../../../../../../actions/apmLocationInfoAttachment';
import { fetchLocationInfoFields as fetchLocationInfoFieldsAction } from '../../../../../../actions/locationInfoValues';
import { reactFinalFormHOC, useFormValues } from '../../../../../../form';
import { notBlank } from '../../../../../../utils/validate';
import Attachments from './Attachments';
import InfoFormField from './InfoFormField';

const useStyles = makeStyles(theme => ({
    paper: {
        display: 'block',
        marginBottom: theme.spacing(2),
        padding: theme.spacing(2),
    },
}));

function Info({
    apmId,
    disabled,
    deleteLocationInfoAttachment,
    fetchItem,
    fetchLocationInfoFields,
    handleSubmit,
    id,
    item,
    ItemFormProps,
    locationInfoId,
    preItems: { locationInfoFields, locationInfoTitles },
    submitting,
    uploadLocationInfoAttachment,
}) {
    const paperStyle = useStyles().paper;

    const addAttachment = async (fields, attachment) => {
        const formData = new FormData();
        formData.append('file', attachment);

        if (locationInfoId) {
            await uploadLocationInfoAttachment({ apmId, formData, id: locationInfoId });
            await fetchItem({ apmId, id: locationInfoId });
        } else {
            fields.push({ contentType: attachment.type, filename: attachment.name, formData });
        }
    };

    const removeAttachment = async (fields, i) => {
        const attachment = fields.value[i];

        if (locationInfoId) {
            await deleteLocationInfoAttachment({
                apmId,
                attachmentId: attachment.id,
                id: locationInfoId,
            });
            await fetchItem({ apmId, id: locationInfoId });
        } else {
            fields.remove(i);
        }
    };

    const { title } = useFormValues(['title'], { ignoreFocused: true });
    useEffect(() => {
        // re-fetch on title change or after submission
        if (title && !submitting) {
            fetchLocationInfoFields({ locationInfoTitle: title });
        }
    }, [fetchLocationInfoFields, submitting, title]);

    const titleOptions = useMemo(() => {
        const options = [...locationInfoTitles].sort();
        if (item?.title) {
            options.unshift(item.title);
        }
        return options;
    }, [item?.title, locationInfoTitles]);

    return (
        <ItemForm
            back={!!window.BACK_BUTTON}
            form={id}
            handleSubmit={handleSubmit}
            id={id}
            submitting={submitting}
            {...ItemFormProps}
        >
            <Field component="input" hidden id={`${id}Id`} name="id" />
            <Field component="input" hidden id={`${id}ApmId`} name="apmId" />
            <Paper className={paperStyle} id={`${id}Paper`} square>
                <Field
                    component={SuggestionField}
                    fullWidth
                    id={`${id}Title`}
                    label="Title *"
                    name="title"
                    options={titleOptions}
                    validate={notBlank}
                />
            </Paper>
            <Paper className={paperStyle} id={`${id}Paper`} square>
                <DndProvider backend={HTML5Backend}>
                    <FieldArray
                        component={InfoFormField}
                        disabled={disabled}
                        id={`${id}Fields`}
                        locationInfoFields={locationInfoFields}
                        name="labelFields"
                    />
                </DndProvider>
            </Paper>
            <Paper className={paperStyle} id={`${id}Paper`} square>
                <Grid container spacing={2}>
                    <Grid item md={6} xs>
                        <Field
                            component={TextField}
                            fullWidth
                            id={`${id}Notes`}
                            label="Notes"
                            minRows={5}
                            multiline
                            name="notes"
                        />
                    </Grid>
                    <Grid item md={6} xs>
                        <FieldArray
                            addAttachment={addAttachment}
                            apmId={apmId}
                            component={Attachments}
                            disabled={disabled}
                            fetchItem={fetchItem}
                            id={`${id}Attachments`}
                            locationInfoId={locationInfoId}
                            name="attachments"
                            removeAttachment={removeAttachment}
                        />
                    </Grid>
                </Grid>
            </Paper>
        </ItemForm>
    );
}

const InfoForm = reactFinalFormHOC()(Info);

const mapStateToProps = (state, { apmId, item }) =>
    item
        ? {
              apmId,
              initialValues: {
                  apmId,
                  attachments: item.attachments,
                  id: item.id,
                  labelFields: item.fields.map((field, i) => ({ ...field, i })),
                  notes: item.notes,
                  title: item.title,
              },
              locationInfoId: item.id,
          }
        : { apmId, initialValues: { apmId, attachments: [], labelFields: [] } };

const mapDispatchToProps = dispatch =>
    bindActionCreators(
        {
            deleteLocationInfoAttachment: deleteLocationInfoAttachmentAction,
            fetchLocationInfoFields: fetchLocationInfoFieldsAction,
            uploadLocationInfoAttachment: uploadLocationInfoAttachmentAction,
        },
        dispatch
    );

const ConnectedInfoForm = connect(mapStateToProps, mapDispatchToProps)(InfoForm);

export default ConnectedInfoForm;
